<template>
  <div
    :class="{
      'flex flex-col gap-md': !stack,
      'flex flex-col gap-md mt-md sm:flex-row': stack
    }"
  >
    <transition name="fade-up">
      <div v-if="channelAdvisorModalOpen" class="channel-advisor-modal">
        <div class="channel-advisor-modal--content">
          <span
            @click="handleCloseChannelAdvisorModal"
            class="channel-advisor-modal--close"
          >
            <SloIcon name="XMark" />
          </span>
          <iframe
            :src="getChannelAdvisorIFrameSource"
            frameborder="0"
            class="channel-advisor-modal--iframe"
          ></iframe>
        </div>
      </div>
    </transition>
    <transition name="fade-up">
      <SloModal
        v-if="whereToBuyModalOpen"
        :form="false"
        :maxWidth="900"
        @dismiss="whereToBuyModalOpen = false"
        class="slo-rep-locator-modal"
      >
        <template v-slot:title>
          <p v-html="productTitle" class="m-0" />
          <p
            v-if="productType !== 'part' && productDescription"
            v-html="productDescription"
            :class="{
              'hidden-xs': true,
              'text-lg': true,
              'hidden': productTitle === productDescription
            }"
          />
        </template>
        <SloRepLocator />
      </SloModal>
    </transition>
    <SloButton
      v-if="channelAdvisorEnabled && showChannelAdvisor"
      @click="handleOpenChannelAdvisorModal(sku)"
    >
      {{ 'Buy Now' | t }}
    </SloButton>
    <SloButton
      v-else-if="showWhereToBuy"
      :fill="whereToBuyButtonFill"
      @click="handleOpenWhereToBuyModal()"
    >
      <span>{{ 'Where to Buy' | t }}</span>
    </SloButton>
    <SloButton
      v-if="showDownloadModelSpec"
      fill="outline"
      target="_blank"
      @click="trackClick()"
      :href="getSpecSheetLink(sku)"
    >
      <span class="no-wrap">{{ 'Download Model Spec' | t }}</span>
    </SloButton>
  </div>
</template>

<script>
import t from '../../main/i18n';
import {getSpecSheetPath} from '../../main/utils';
import SloIcon from '../../../lib/components/SloIcon.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import SloModal from '../../../lib/components/SloModal.vue';
import SloTabs from '../../../lib/components/SloTabs.vue';
import SloTab from '../../../lib/components/SloTab.vue';
import SloRepLocator from '../../../lib/components/SloRepLocator.vue';

export default {
  name: 'VariationActions',
  components: {
    SloIcon,
    SloButton,
    SloModal,
    SloTabs,
    SloTab,
    SloRepLocator
  },
  filters: {
    t
  },
  props: {
    sku: {type: String, required: true},
    productType: {type: String, required: true},
    productTitle: {type: String, required: true},
    productDescription: {type: String, required: false},
    productContext: {type: Object, required: false},
    channelAdvisorEnabled: {type: Boolean, default: false},
    showDownloadModelSpec: {type: Boolean, default: true},
    showWhereToBuy: {type: Boolean, default: true},
    showChannelAdvisor: {type: Boolean, default: true},
    whereToBuyButtonFill: {type: String, default: 'solid'},
    stack: {type: Boolean, default: false}
  },
  data() {
    return {
      channelAdvisorModalOpen: false,
      whereToBuyModalOpen: false,
      getChannelAdvisorIFrameSource: null
    };
  },
  updated() {
    let repLocatorModalContainer = document.querySelector('.slo-rep-locator-modal');

    if (repLocatorModalContainer) {
      repLocatorModalContainer.style.setProperty(
        '--slo-modal-header-height',
        document.querySelector('.slo-modal-frame header').offsetHeight + 'px'
      );
    }
  },
  methods: {
    getSpecSheetLink(sku) {
      return getSpecSheetPath(sku, this.langcode);
    },
    trackClick() {
      if (!this.productContext) return;
      const {analytics} = this.productContext;
      window.ga('send', 'event', {
        eventCategory: analytics.eventCategory,
        eventAction: 'download',
        eventLabel: analytics.eventLabel
      });
    },
    handleOpenChannelAdvisorModal(sku) {
      this.channelAdvisorModalOpen = true;
      this.getChannelAdvisorIFrameSource =
        `https://where-to-buy.co/widgets/core/BuyOnlineBuyLocalV2/index.html?pid=12046225&model=${sku}`;
    },
    handleCloseChannelAdvisorModal() {
      this.channelAdvisorModalOpen = false;
      this.getChannelAdvisorIFrameSource = null;
    },
    handleOpenWhereToBuyModal() {
      this.whereToBuyModalOpen = true;
    }
  }
};
</script>

<style lang="scss">
@import '../../../scss/system';

.channel-advisor-modal {
  position: fixed;
  z-index: 20000;
  inset: 0;
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  overflow: auto;
  background-color: rgba(#dedede, 0.8);
}

.fade-up-enter, .fade-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
.fade-up-enter-active, .fade-up-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.channel-advisor-modal--content {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr;
  place-items: end;
  gap: 16px;
  height: auto;
  width: calc(min(100% - 30px, 800px));
  margin-inline: auto;
  padding-block: 16px;
}

.channel-advisor-modal--iframe {
  width: 100%;
  height: 100%;
  min-height: 667px;
  border-radius: 12px;

  @media (min-width: 710px) {
    min-height: 600px;
  }
}

.channel-advisor-modal--close {
  display: flex;
  height: 16px;
  color: rgba($color-gray-900, 0.7);
  font-weight: bold;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: rgba($color-gray-900, 1);
  }
}

.slo-rep-locator-modal {
  .slo-modal-frame {
    min-height: 700px !important;
    max-height: 700px !important;
    padding: 0 !important;

    .slo-modal-content {
      padding: 0 !important;
    }

    header {
      position: sticky !important;
      top: 0;
      z-index: 100;
      padding: 16px;
      background-color: $color-blue-400;
      color: $color-white;
      overflow: initial;

      &:before, &:after {
        display: none;
      }

      .slo-modal-title {
        font-weight: 500;
      }

      button {
        padding: 0 !important;

        &:hover, &:focus {
          color: $color-gray-400;
        }
      }
    }
  }
}
</style>
